/* ============================================================
   PURE BIRKDALE — Shade Finder (ported from shade-finder.jsx)
   Match skin tone to the nearest shade from a photo or live
   camera. sRGB→Lab + weighted distance, skin-pixel filter,
   user-positioned sample reticle. Runs entirely client-side —
   nothing is uploaded or stored.

   Requires on the page:
     [data-product-json]  — Shopify product JSON (variants)
     [data-shades-json]   — custom.shades metafield JSON
     [data-open-shade-finder] — trigger button(s)
   ============================================================ */
(function () {
  'use strict';

  var productJson = document.querySelector('[data-product-json]');
  var shadesJson = document.querySelector('[data-shades-json]');
  if (!productJson || !shadesJson) return;
  var product = JSON.parse(productJson.textContent);
  var shades = JSON.parse(shadesJson.textContent);

  /* ---- colour maths (1:1 with the design prototype) ---- */
  function hexToRgb(hex) {
    var h = hex.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  function srgbToLab(r, g, b) {
    var R = r / 255, G = g / 255, B = b / 255;
    var f = function (c) { return c > 0.04045 ? Math.pow((c + 0.055) / 1.055, 2.4) : c / 12.92; };
    R = f(R); G = f(G); B = f(B);
    var X = R * 0.4124 + G * 0.3576 + B * 0.1805;
    var Y = R * 0.2126 + G * 0.7152 + B * 0.0722;
    var Z = R * 0.0193 + G * 0.1192 + B * 0.9505;
    X /= 0.95047; Z /= 1.08883;
    var g2 = function (t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; };
    var fx = g2(X), fy = g2(Y), fz = g2(Z);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }
  function labDist(a, b) {
    /* weight lightness a touch less so undertone matters */
    var dL = (a[0] - b[0]) * 0.8, da = a[1] - b[1], db = a[2] - b[2];
    return Math.sqrt(dL * dL + da * da + db * db);
  }
  function isSkin(r, g, b) {
    if (r < 60 || r > 250) return false;
    if (g < 30 || b < 15) return false;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    if (r <= g) return false;
    if (g < b) return false;
    if (mx - mn < 8) return false;
    if ((r - g) < 4) return false;
    return true;
  }
  function sampleSkin(data, w, h, box) {
    var x0 = Math.max(0, Math.floor(box.x * w)), x1 = Math.min(w, Math.ceil((box.x + box.s) * w));
    var y0 = Math.max(0, Math.floor(box.y * h)), y1 = Math.min(h, Math.ceil((box.y + box.s) * h));
    var skin = [], x, y, i;
    for (y = y0; y < y1; y += 2) for (x = x0; x < x1; x += 2) {
      i = (y * w + x) * 4;
      if (data[i + 3] < 200) continue;
      if (isSkin(data[i], data[i + 1], data[i + 2])) skin.push([data[i], data[i + 1], data[i + 2]]);
    }
    var pool = skin;
    if (skin.length < 30) {
      pool = [];
      for (y = y0; y < y1; y += 2) for (x = x0; x < x1; x += 2) {
        i = (y * w + x) * 4;
        pool.push([data[i], data[i + 1], data[i + 2]]);
      }
    }
    if (!pool.length) return null;
    pool.sort(function (p, q) {
      return (0.299 * p[0] + 0.587 * p[1] + 0.114 * p[2]) - (0.299 * q[0] + 0.587 * q[1] + 0.114 * q[2]);
    });
    var lo = Math.floor(pool.length * 0.2), hi = Math.ceil(pool.length * 0.8);
    var mid = pool.slice(lo, hi);
    var R = 0, G = 0, B = 0;
    mid.forEach(function (p) { R += p[0]; G += p[1]; B += p[2]; });
    return [Math.round(R / mid.length), Math.round(G / mid.length), Math.round(B / mid.length)];
  }
  function rankShades(rgb) {
    var lab = srgbToLab(rgb[0], rgb[1], rgb[2]);
    return shades.map(function (sh) {
      var srgb = hexToRgb(sh.hex);
      return { sh: sh, d: labDist(lab, srgbToLab(srgb[0], srgb[1], srgb[2])) };
    }).sort(function (a, b) { return a.d - b.d; });
  }
  function confidenceLabel(d) {
    if (d < 6) return 'Excellent match';
    if (d < 12) return 'Strong match';
    if (d < 20) return 'Good match';
    return 'Closest available — consider a pro colour match';
  }

  /* ---- modal (built once, shown on demand) ---- */
  var state = { mode: null, imgURL: null, box: { x: 0.38, y: 0.40, s: 0.24 }, stream: null };
  var overlay = document.createElement('div');
  overlay.className = 'finder-overlay';
  overlay.hidden = true;
  overlay.innerHTML =
    '<div class="finder-bg" data-sf-close></div>' +
    '<div class="finder-modal">' +
    '  <div class="finder-head">' +
    '    <div><span class="eyebrow">Shade Finder</span><h3>Find your perfect match</h3></div>' +
    '    <button type="button" class="icon-btn" data-sf-close aria-label="Close">✕</button>' +
    '  </div>' +
    '  <div class="finder-body" data-sf-body></div>' +
    '</div>';
  document.body.appendChild(overlay);
  var body = overlay.querySelector('[data-sf-body]');

  function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }

  function stopCam() {
    if (state.stream) { state.stream.getTracks().forEach(function (t) { t.stop(); }); state.stream = null; }
  }
  function close() { stopCam(); overlay.hidden = true; }
  overlay.addEventListener('click', function (e) { if (e.target.closest('[data-sf-close]')) close(); });

  function open() {
    overlay.hidden = false;
    overlay.classList.add('show');
    renderStart();
  }
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-open-shade-finder]');
    if (t) { e.preventDefault(); open(); }
  });

  function renderStart() {
    state.mode = null; state.imgURL = null; stopCam();
    body.innerHTML =
      '<div class="finder-start">' +
      '  <p class="finder-lead">Match your skin tone to <b>' + esc(product.title) + '</b> in seconds. For the truest result, use natural light and a bare-skin area like your jaw or inner wrist.</p>' +
      '  <div class="finder-choices">' +
      '    <button type="button" class="finder-choice" data-sf-camera><span class="fc-ic">📷</span><b>Use camera</b><span>Live match with your webcam</span></button>' +
      '    <label class="finder-choice"><span class="fc-ic">✨</span><b>Upload a photo</b><span>Use a well-lit selfie</span><input type="file" accept="image/*" hidden data-sf-upload></label>' +
      '  </div>' +
      '  <div class="finder-fine">🔒 Your photo is processed privately on your device — nothing is uploaded or stored.</div>' +
      '</div>';
    body.querySelector('[data-sf-camera]').addEventListener('click', startCamera);
    body.querySelector('[data-sf-upload]').addEventListener('change', onUpload);
  }

  function startCamera() {
    state.mode = 'camera'; state.imgURL = null;
    body.innerHTML =
      '<div class="finder-stage-wrap">' +
      '  <div class="finder-stage finder-mirror" data-sf-stage>' +
      '    <video playsinline muted data-sf-video></video>' +
      reticleHtml(false) +
      '    <div class="finder-guide">Line up your cheek or jaw inside the circle</div>' +
      '  </div>' +
      '  <div class="finder-actions">' +
      '    <button type="button" class="btn btn-ghost" data-sf-back>Back</button>' +
      '    <button type="button" class="btn btn-primary btn-lg" data-sf-capture disabled>✨ Capture &amp; match</button>' +
      '  </div>' +
      '</div>';
    body.querySelector('[data-sf-back]').addEventListener('click', renderStart);
    var video = body.querySelector('[data-sf-video]');
    var capture = body.querySelector('[data-sf-capture]');
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 960 } }, audio: false })
      .then(function (stream) {
        state.stream = stream;
        video.srcObject = stream;
        video.play().catch(function () {});
        capture.disabled = false;
      })
      .catch(function (e) {
        camError(e && e.name === 'NotAllowedError'
          ? 'Camera permission was blocked. Allow access or upload a photo instead.'
          : 'No camera available — upload a photo instead.');
      });
    capture.addEventListener('click', function () {
      var cv = document.createElement('canvas');
      cv.width = video.videoWidth; cv.height = video.videoHeight;
      cv.getContext('2d').drawImage(video, 0, 0);
      state.imgURL = cv.toDataURL('image/jpeg', 0.9);
      stopCam();
      renderStill();
    });
  }

  function camError(msg) {
    body.innerHTML =
      '<div class="finder-camerr"><p>' + esc(msg) + '</p>' +
      '<label class="btn btn-primary">Upload a photo<input type="file" accept="image/*" hidden data-sf-upload></label></div>';
    body.querySelector('[data-sf-upload]').addEventListener('change', onUpload);
  }

  function onUpload(e) {
    var f = e.target.files && e.target.files[0];
    if (!f) return;
    var fr = new FileReader();
    fr.onload = function () { state.mode = 'photo'; state.imgURL = fr.result; renderStill(); };
    fr.readAsDataURL(f);
  }

  function reticleHtml(drag) {
    var b = state.box;
    return '<div class="reticle' + (drag ? ' drag' : '') + '" data-sf-reticle style="left:' + b.x * 100 + '%;top:' + b.y * 100 + '%;width:' + b.s * 100 + '%;height:' + b.s * 100 + '%"></div>';
  }

  function renderStill() {
    body.innerHTML =
      '<div class="finder-stage-wrap">' +
      '  <div class="finder-stage' + (state.mode === 'camera' ? ' finder-mirror' : '') + '" data-sf-stage>' +
      '    <img src="' + state.imgURL + '" alt="Your photo">' +
      reticleHtml(true) +
      '    <div class="finder-guide">Drag the circle onto an even patch of skin</div>' +
      '  </div>' +
      '  <div class="finder-actions">' +
      '    <button type="button" class="btn btn-ghost" data-sf-back>Retake</button>' +
      '    <button type="button" class="btn btn-primary btn-lg" data-sf-analyse>✨ Analyse my skin tone</button>' +
      '  </div>' +
      '</div>';
    body.querySelector('[data-sf-back]').addEventListener('click', renderStart);
    body.querySelector('[data-sf-analyse]').addEventListener('click', analyse);

    var stage = body.querySelector('[data-sf-stage]');
    var reticle = body.querySelector('[data-sf-reticle]');
    var dragging = false;
    function move(e) {
      if (!dragging) return;
      var r = stage.getBoundingClientRect();
      var cx = ((e.touches ? e.touches[0].clientX : e.clientX) - r.left) / r.width;
      var cy = ((e.touches ? e.touches[0].clientY : e.clientY) - r.top) / r.height;
      var b = state.box;
      b.x = Math.min(1 - b.s, Math.max(0, cx - b.s / 2));
      b.y = Math.min(1 - b.s, Math.max(0, cy - b.s / 2));
      reticle.style.left = b.x * 100 + '%';
      reticle.style.top = b.y * 100 + '%';
    }
    stage.addEventListener('mousedown', function (e) { dragging = true; move(e); });
    stage.addEventListener('touchstart', function (e) { dragging = true; move(e); });
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move);
    window.addEventListener('mouseup', function () { dragging = false; });
    window.addEventListener('touchend', function () { dragging = false; });
  }

  function analyse() {
    var im = new Image();
    im.onload = function () {
      var w = im.naturalWidth, h = im.naturalHeight;
      var cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      var ctx = cv.getContext('2d');
      ctx.drawImage(im, 0, 0);
      var box = state.box;
      if (state.mode === 'camera') box = { x: 1 - state.box.x - state.box.s, y: state.box.y, s: state.box.s }; /* un-mirror */
      var rgb = sampleSkin(ctx.getImageData(0, 0, w, h).data, w, h, box);
      if (!rgb) { renderError(); return; }
      renderResult(rgb, rankShades(rgb));
    };
    im.onerror = renderError;
    im.src = state.imgURL;
  }

  function renderError() {
    body.innerHTML =
      '<div class="finder-camerr"><p>We couldn\'t read a clear skin tone there. Try moving the circle onto a brighter, even patch of skin.</p>' +
      '<button type="button" class="btn btn-primary" data-sf-retry>Try again</button></div>';
    body.querySelector('[data-sf-retry]').addEventListener('click', renderStill);
  }

  function variantForShade(code) {
    return product.variants.find(function (v) {
      return v.options.some(function (o) { return o === code || o.indexOf(code) === 0; });
    });
  }

  function renderResult(rgb, ranked) {
    var best = ranked[0], alts = ranked.slice(1, 3);
    body.innerHTML =
      '<div class="finder-result">' +
      '  <div class="fr-detected">' +
      '    <span class="fr-chip" style="background: rgb(' + rgb.join(',') + ')"></span>' +
      '    <div><b>Your detected tone</b><span>We found your closest match across ' + shades.length + ' shades</span></div>' +
      '  </div>' +
      '  <div class="fr-best">' +
      '    <div class="fr-best-sw" style="background:' + best.sh.hex + '">✓</div>' +
      '    <div class="fr-best-info">' +
      '      <span class="eyebrow">Your best match</span>' +
      '      <h3>' + esc(best.sh.code) + ' · ' + esc(best.sh.name) + '</h3>' +
      '      <div class="fr-confidence">' + confidenceLabel(best.d) + '</div>' +
      '    </div>' +
      '    <div class="fr-best-actions">' +
      '      <button type="button" class="btn btn-primary" data-sf-pick="' + esc(best.sh.code) + '">Select shade</button>' +
      '      <button type="button" class="btn btn-ghost btn-sm" data-sf-add="' + esc(best.sh.code) + '">Add to bag</button>' +
      '    </div>' +
      '  </div>' +
      (alts.length ? (
        '<div class="fr-alts"><span class="fr-alts-label">You may also like</span><div class="fr-alts-row">' +
        alts.map(function (a) {
          return '<button type="button" class="fr-alt" data-sf-pick="' + esc(a.sh.code) + '">' +
            '<span class="fr-alt-sw" style="background:' + a.sh.hex + '"></span>' +
            '<span><b>' + esc(a.sh.code) + '</b><span>' + esc(a.sh.name) + '</span></span></button>';
        }).join('') +
        '</div></div>'
      ) : '') +
      '  <div class="finder-actions finder-actions-split">' +
      '    <button type="button" class="btn btn-ghost btn-sm" data-sf-restart>Start over</button>' +
      '    <span class="finder-fine finder-fine-flush">Still unsure? <a data-book-now>Book a colour match</a></span>' +
      '  </div>' +
      '</div>';
    body.querySelector('[data-sf-restart]').addEventListener('click', renderStart);
    Array.prototype.forEach.call(body.querySelectorAll('[data-sf-pick]'), function (b) {
      b.addEventListener('click', function () {
        if (window.PBSelectShade) window.PBSelectShade(matchOptionValue(b.dataset.sfPick));
        close();
      });
    });
    var add = body.querySelector('[data-sf-add]');
    if (add) add.addEventListener('click', function () {
      var v = variantForShade(add.dataset.sfAdd);
      if (v && window.PBCart) {
        if (window.PBSelectShade) window.PBSelectShade(matchOptionValue(add.dataset.sfAdd));
        close();
        window.PBCart.add(v.id, 1);
      }
    });
  }

  /* option values may be "10N" or "10N · Light Neutral" — map code → actual value */
  function matchOptionValue(code) {
    var i, j;
    for (i = 0; i < product.variants.length; i++) {
      var opts = product.variants[i].options;
      for (j = 0; j < opts.length; j++) {
        if (opts[j] === code || opts[j].indexOf(code) === 0) return opts[j];
      }
    }
    return code;
  }
})();
